/**
 * Calendar Event Component
 * 
 * Custom event rendering with color coding and lock indicators
 */

import React from 'react';
import { Lock } from 'lucide-react';
import type { CalendarEventProps } from '../../types/calendar';
import { getEventColor } from './CalendarConfig';
import { formatEventTimeRange } from '../../utils/calendarHelpers';

export const CalendarEvent: React.FC<CalendarEventProps> = ({
  event,
  compact = false,
  onClick,
}) => {
  const colors = getEventColor(event.colorScheme);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(event);
  };

  if (compact) {
    // Compact mode for high-density days
    return (
      <div
        onClick={handleClick}
        className="px-2 py-1 mb-1 rounded text-xs font-medium cursor-pointer transition-all hover:shadow-md truncate"
        style={{
          backgroundColor: colors.bg,
          color: colors.text,
          borderLeft: `3px solid ${colors.border}`,
        }}
        title={`${event.title}${event.isLocked ? ' (Locked)' : ''}`}
      >
        <div className="flex items-center justify-between">
          <span className="truncate flex-1">{event.title}</span>
          {event.isLocked && (
            <Lock className="w-3 h-3 ml-1 flex-shrink-0" />
          )}
        </div>
      </div>
    );
  }

  // Standard mode
  return (
    <div
      onClick={handleClick}
      className="px-2 py-1.5 mb-1 rounded shadow-sm hover:shadow-md cursor-pointer transition-all"
      style={{
        backgroundColor: event.isLocked ? '#f3f4f6' : colors.bg,
        color: event.isLocked ? '#374151' : colors.text,
        borderLeft: `4px solid ${event.isLocked ? '#8b5cf6' : colors.border}`,
      }}
    >
      {/* Event Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Time */}
          {!event.allDay && (
            <div className="text-xs opacity-90 mb-0.5">
              {formatEventTimeRange(event)}
            </div>
          )}
          
          {/* Title */}
          <div className="text-sm font-medium truncate">
            {event.title}
          </div>
          
          {/* Category Badge */}
          {event.categoryName && (
            <div className="text-xs opacity-80 mt-0.5">
              {event.categoryName}
            </div>
          )}
        </div>

        {/* Lock Indicator */}
        {event.isLocked && (
          <div className="ml-2 flex-shrink-0">
            <Lock className="w-4 h-4 text-purple-600" />
          </div>
        )}
      </div>

      {/* Description (if space allows) */}
      {event.description && !compact && (
        <div className="text-xs opacity-75 mt-1 line-clamp-2">
          {event.description}
        </div>
      )}
    </div>
  );
};

